import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getErrorDefinition, getErrorMessage } from '../lib/errors';

const ToastContext = createContext(null);

let toastId = 0;

// Icons for each toast type
const ToastIcons = {
  success: (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  info: (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((options) => {
    const id = ++toastId;

    // Support both string and object arguments
    const toastData = typeof options === 'string'
      ? { message: options, type: 'info' }
      : options;

    const {
      message,
      title,
      type = 'info',
      duration = 5000,
      action,
    } = toastData;

    setToasts((prev) => [...prev, { id, message, title, type, action }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = {
    // Simple methods for quick use
    success: (message, options = {}) => addToast({ message, type: 'success', ...options }),
    error: (messageOrError, options = {}) => {
      // Handle error objects specially
      if (messageOrError && typeof messageOrError === 'object' && (messageOrError.code || messageOrError.errorCode)) {
        const errorDef = getErrorDefinition(messageOrError);
        return addToast({
          title: errorDef.message,
          message: errorDef.suggestion,
          type: 'error',
          ...options,
        });
      }
      return addToast({ message: messageOrError, type: 'error', ...options });
    },
    info: (message, options = {}) => addToast({ message, type: 'info', ...options }),
    warning: (message, options = {}) => addToast({ message, type: 'warning', ...options }),

    // Rich toast with title and description
    show: (options) => addToast(options),

    // Dismiss a specific toast
    dismiss: removeToast,

    // Dismiss all toasts
    dismissAll: () => setToasts([]),
  };

  return (
    <ToastContext.Provider value={{ toasts, toast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, removeToast }) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-toast flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onClose }) {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 150);
  };

  const styles = {
    success: {
      bg: 'bg-midnight-light',
      border: 'border-success/30',
      iconBg: 'bg-success/20',
      icon: 'text-success',
      title: 'text-success',
    },
    error: {
      bg: 'bg-midnight-light',
      border: 'border-error/30',
      iconBg: 'bg-error/20',
      icon: 'text-error',
      title: 'text-error',
    },
    warning: {
      bg: 'bg-midnight-light',
      border: 'border-warning/30',
      iconBg: 'bg-warning/20',
      icon: 'text-warning',
      title: 'text-warning',
    },
    info: {
      bg: 'bg-midnight-light',
      border: 'border-primary/30',
      iconBg: 'bg-primary/20',
      icon: 'text-primary',
      title: 'text-primary',
    },
  }[toast.type] || styles.info;

  const IconComponent = ToastIcons[toast.type] || ToastIcons.info;

  return (
    <div
      className={`
        ${styles.bg} ${styles.border}
        border rounded-xl shadow-xl
        p-4 pointer-events-auto
        transform transition-all duration-150 ease-out
        ${isLeaving ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0 animate-slide-in-right'}
      `}
      role="alert"
    >
      <div className="flex gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${styles.iconBg} flex items-center justify-center`}>
          <IconComponent className={`w-4 h-4 ${styles.icon}`} />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          {toast.title && (
            <p className={`text-sm font-semibold ${styles.title}`}>{toast.title}</p>
          )}
          {toast.message && (
            <p className={`text-sm text-cream/80 ${toast.title ? 'mt-0.5' : ''}`}>
              {toast.message}
            </p>
          )}
          {toast.action && (
            <button
              onClick={() => {
                toast.action.onClick?.();
                handleClose();
              }}
              className="mt-2 text-sm font-medium text-primary hover:text-primary-light transition-colors"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 -mt-1 -mr-1 text-cream/40 hover:text-cream/80 hover:bg-cream/10 rounded-lg transition-colors"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
